import { apiClient } from '../lib/apiClient';

/** 'manual' = entered by the user, 'pump' = logged automatically for a dosing-pump plan. */
export type DosingLogSource = 'manual' | 'pump';

/** A logged dose, exactly as `GET /api/v1/aquariums/:id/dosing-logs` returns it. */
export interface DosingLog {
  readonly id: string;
  readonly tank_id: string;
  readonly schedule_id: string | null;
  readonly product_name: string;
  readonly dose_amount: number;
  readonly dose_unit: string;
  readonly photo_url: string | null;
  readonly logged_at: string;
  readonly notes: string | null;
  readonly created_at: string;
  readonly source: DosingLogSource;
  readonly scheduled_for: string | null;
}

export interface CreateDosingLogInput {
  readonly product_name: string;
  readonly dose_amount: number;
  readonly dose_unit: string;
  readonly schedule_id?: string | null;
  readonly notes?: string | null;
  readonly logged_at?: string;
}

export interface UpdateDosingLogInput {
  readonly dose_amount?: number;
  readonly notes?: string | null;
}

const base = (aquariumId: string): string => `/api/v1/aquariums/${aquariumId}/dosing-logs`;

/** The latest logged doses (newest first). Reading also catches up due pump doses server-side. */
export async function listDosingLogs(aquariumId: string, limit = 20): Promise<DosingLog[]> {
  const json = await apiClient.get<{ data: DosingLog[] }>(`${base(aquariumId)}?limit=${limit}`);
  return json.data;
}

export async function createDosingLog(aquariumId: string, input: CreateDosingLogInput): Promise<DosingLog> {
  const json = await apiClient.post<{ data: DosingLog }>(base(aquariumId), input);
  return json.data;
}

export async function updateDosingLog(aquariumId: string, logId: string, input: UpdateDosingLogInput): Promise<DosingLog> {
  const json = await apiClient.patch<{ data: DosingLog }>(`${base(aquariumId)}/${logId}`, input);
  return json.data;
}

export async function deleteDosingLog(aquariumId: string, logId: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/${logId}`);
}
